// External
import { Component, Input, HostListener, OnInit } from '@angular/core';

// Internal
import { TooltipService } from '../../../services/global/tooltip/tooltip.service';

export enum TooltipPosition {
    TOP = 'top',
    RIGHT = 'right',
    LEFT = 'left',
    BOTTOM = 'bottom'
}

const MIN_WIDTH = 150;
const MAX_WIDTH = 280;
const MIN_HEIGHT = 100;
const NO_SPACES = 1;
const MAX_CONTENT = 'max-content';
const TITLE_LENGTH_FOR_MIN_WIDTH = 15;
const TITLE_LENGTH_FOR_MAX_WIDTH = 30;

@Component({
  selector: 'ui-tooltip',
  template: `<div class="tooltip {{position}}" #tooltip
                [style.top.px]="triggerCoords.top + offsetTop"
                [style.left.px]="triggerCoords.left + offsetLeft"
                [style.width]="width"
                [ngClass]="{'word-break': breakTitle}">
	                <div class="tooltip-content {{color}}" [innerHTML]="title"></div>
            </div>`
})
export class UiTooltipComponent implements OnInit {

    @Input() ref: any;
    @Input() title: string;
    @Input() position: TooltipPosition;
    @Input() color: string;
    @Input() width: number | string;

    // positioning
    public triggerCoords: any;
    public offsetLeft = 0;
    public offsetTop = 0;
    public breakTitle = false;

    private windowSize = {
        width: 0,
        height: 0
    };

    constructor(private readonly tooltipService: TooltipService) { }

    ngOnInit() {
        this.windowSize = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        this.positionTooltip();
    }

    @HostListener('window: mousewheel', ['$event']) onScroll(event) {
        this.tooltipService.removeTooltip();
    }

    /**
     *
     * Calculates the tooltip position
     * svgElements doesn't have offsetWidth/height
     * @private
     * @memberof UiTooltipComponent
     */
    private positionTooltip() {
        this.calculatePosition();
        let svgBBox = null;
        const refBox = {
            width: this.ref.nativeElement.offsetWidth,
            height: this.ref.nativeElement.offsetHeight
        };
        //Daca nu are offsetWidth sau offsetHeight presupun ca e parte din SVG
        if (!refBox.width || !refBox.height) {
            svgBBox = this.ref.nativeElement?.getBBox();
        }

        switch (this.position) {
            case TooltipPosition.BOTTOM: {
                this.offsetLeft = refBox.width ? refBox.width/2 : svgBBox.width;
                this.offsetTop = refBox.height ?? svgBBox.height;
                break;
            }
            case TooltipPosition.LEFT: {
                this.offsetTop = refBox.height ? refBox.height/2 : svgBBox.height;
                break;
            }
            case TooltipPosition.RIGHT: {
                this.offsetLeft = refBox.width ?? svgBBox.width;
                this.offsetTop = refBox.height ? refBox.height/2 : svgBBox.height;
                break;
            }
            default: {
                //Default TOP
                this.offsetLeft = refBox.width ? refBox.width/2 : svgBBox.width;
                break
            }
        }

        if (typeof(this.width) === 'number' ) {
            this.width = `${this.width}px`;
        }

        if (Number.isNaN(this.offsetLeft)) {
            this.offsetLeft = 0;
        }

        if (Number.isNaN(this.offsetTop)) {
            this.offsetTop = 0;
        }
    }

    /**
     * Evaluate and set recomanded position for tooltip.
     * If the tooltip is located in the safe zone the position will be left unmodified.
     *  -+--+--+-
     * |_|__|__|_|
     * |_|__|__|_|
     *  -+--+--+-
     *
     * |_| -> value lower than width or MIN_WIDTH
     * |__| -> safe zone for all position
     * |_ -> just RIGHT position (and if is a huge element will be on TOP OR BOTTOM)
     * _| -> just LEFT position
     *
     *  --+-- -> no TOP Position
     * |__|__|
     *
     * |__|__|
     *  --+-- -> no BOTTOM Position
     * @private
     * @memberof UiTooltipComponent
     */
    private calculatePosition(): void {
        const tooltipHolder = this.ref.nativeElement;
        this.triggerCoords = tooltipHolder.getBoundingClientRect();
        const tooltipWidth = typeof(this.width) === 'number' ? this.width : MIN_WIDTH;

        const positionCondition = {
            top: this.windowSize.height - this.triggerCoords.top < MIN_HEIGHT,
            bottom: this.triggerCoords.top < MIN_HEIGHT,
            left: this.windowSize.width - this.triggerCoords.left < tooltipWidth,
            right: this.triggerCoords.left < tooltipWidth
        };

        const setWidth = () => {
            // If text has one spece it will make it to be on a single line
            // If the title is too long, break it
            if ((this.title.match(/ /g) || []).length > NO_SPACES) {
                this.width = MIN_WIDTH;

                if (this.title.length >= TITLE_LENGTH_FOR_MIN_WIDTH) {
                    this.breakTitle = true;
                }
            } else if (this.title.length >= TITLE_LENGTH_FOR_MAX_WIDTH) {
                this.width = MAX_WIDTH;
                this.breakTitle = true;
            } else {
                this.width = MAX_CONTENT;
            }
        }

        if (positionCondition.left) {
            this.position = TooltipPosition.LEFT;
            setWidth();
        } else if (positionCondition.right) {
            //Verify if is a huge element
            if ((this.windowSize.width - tooltipHolder.offsetWidth)/2 < MIN_WIDTH) {
                if (positionCondition.top) {
                    this.position = TooltipPosition.TOP;
                } else if (positionCondition.bottom) {
                    this.position = TooltipPosition.BOTTOM;
                }
            } else {
                this.position = TooltipPosition.RIGHT;
                setWidth();
            }
        } else if (positionCondition.top) {
            this.position = TooltipPosition.TOP;
        } else if (positionCondition.bottom) {
            this.position = TooltipPosition.BOTTOM;
        }
    }
}
